import { Container } from '@chakra-ui/react';
import {
  ADDRESS_SOURCE_DISPLAY,
  BillGroupSummaryListUi,
  NET_TERMS_DISPLAY,
} from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { useRef, useState } from 'react';
import {
  MBox,
  MButton,
  MFlex,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { SELECTION_COL_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../constants/billGroups';
import { pluralize } from '../../../utils';
import { BillingPageLoadingBar } from '../shared/BillingPageLoadingBar';
import { useManualBillRun } from './useManualBillRun';

const INITIAL_SORTING_STATE = [{ id: 'nextInvoiceDate', desc: false }];

const pinnedLeftColumns = [SELECTION_COL_KEY];

const EMPTY_ARR: BillGroupSummaryListUi[] = [];

export const ManualBillRun = () => {
  const [selectedCount, setSelectedCount] = useState(0);
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const tableRef = useRef<DataTableRef>(null);

  const {
    batchInitIsLoading,
    billGroupQueryResult,
    columns,
    customFieldQueryResult,
    handleInvokeManualBillRun,
    progress,
  } = useManualBillRun();

  const {
    data: billGroups,
    isLoading,
    isRefetching,
    refetch,
  } = billGroupQueryResult;

  const { isLoading: isLoadingCustomFields } = customFieldQueryResult;

  const submitSelectedBillGroups = () => {
    const selectedBillGroups =
      tableRef.current?.getSelectedRows<BillGroupSummaryListUi>() ?? EMPTY_ARR;
    handleInvokeManualBillRun(selectedBillGroups.map(({ id }) => id));
  };

  return (
    <Container
      maxHeight="calc(100vh - 175px)"
      maxWidth="unset"
      px="0"
      position="relative"
      centerContent
      data-testid="bill-run-page"
    >
      <BillingPageLoadingBar
        isVisible={isLoading || isRefetching}
        progress={progress}
      />
      <MPageHeader
        title="Bill Run"
        subTitle={<MText>Progress billing for any eligible bill groups.</MText>}
      />

      {isLoadingCustomFields || isLoading ? (
        <MPageLoader height="50vh" />
      ) : (
        <>
          <MFlex
            mb={2}
            w="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <MFlex alignItems="center" gap={3}>
              <MButton
                variant="primary"
                isLoading={batchInitIsLoading}
                onClick={submitSelectedBillGroups}
                isDisabled={
                  isRefetching || batchInitIsLoading || selectedCount === 0
                }
                type="button"
              >
                Submit Selected Bill Groups
              </MButton>
              {selectedCount > 0 && (
                <MText>
                  {formatInteger(selectedCount)}{' '}
                  {pluralize('bill group', selectedCount)} selected
                </MText>
              )}
            </MFlex>
            <MBox>
              <MButton
                variant="tertiary"
                size="sm"
                onClick={() => tableRef.current?.resetFilters()}
                type="button"
              >
                Reset All Filters
              </MButton>
              <MButton
                variant="tertiary"
                size="sm"
                isLoading={isRefetching}
                onClick={() => refetch()}
                type="button"
              >
                Reload
              </MButton>
            </MBox>
          </MFlex>
          <DataTable
            ref={tableRef}
            columns={columns}
            data={billGroups || EMPTY_ARR}
            pinnedLeftColumns={pinnedLeftColumns}
            enablePagination={false}
            filterDataDisplayMap={{
              status: BILL_GROUP_STATUS_DISPLAY,
              netTerms: NET_TERMS_DISPLAY,
              addressSource: ADDRESS_SOURCE_DISPLAY,
            }}
            enableVirtualization
            clientControlled
            initialSortingState={INITIAL_SORTING_STATE}
            onSelectedRowCountChange={setSelectedCount}
            onVisibleRowCountChange={setVisibleRowCount}
          />
          {billGroups && (
            <MText mt={2} w="100%">
              Showing {formatInteger(visibleRowCount)} of{' '}
              {formatInteger(billGroups.length)} Bill Groups
            </MText>
          )}
        </>
      )}
    </Container>
  );
};
